import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import {
  Loader, Breadcrumbs, SectionRenderer, ComparisonTable, QuickAnswer, FAQ,
  ComparisonCard, ToolCard, Overline, LazyImage, ComparisonVisual, formatDate,
} from "@/components/shared";
import { proxyImg } from "@/lib/api";
import { NotFound } from "@/pages/ToolDetail";
import { AdSlot } from "@/components/Ads";

export default function ComparisonDetail() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); api.comparison(slug).then(setD).catch(() => setD(false)); }, [slug]);
  useSEO(d && d.comparison ? { title: d.comparison.seo_title, description: d.comparison.meta_description } : {});
  if (d === false) return <NotFound />;
  if (!d) return <Loader />;
  const c = d.comparison;
  const rest = (c.body_sections || []).filter((s) => s.heading !== "Quick Answer");

  return (
    <div>
      <div className="klyroo-grid border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Comparisons", to: "/comparisons" }, { label: c.category }, { label: c.title }]} />
          <Overline>{c.category} · Comparison</Overline>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight mt-2 leading-[1.08]">{c.title}</h1>
          <div className="flex items-center gap-4 mt-4 flex-wrap">
            {(c.logos || []).filter(Boolean).map((l, i) => <img key={i} src={proxyImg(l)} alt="" onError={(e) => (e.currentTarget.style.display = "none")} className="w-10 h-10 rounded-lg bg-white p-1.5 object-contain border border-white/10" />)}
            <span className="text-xs font-mono text-slate-500">Updated {formatDate(c.updated_at)} · {c.reading_time} min read</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-[minmax(0,1fr)_300px] gap-8 lg:gap-10">
        <div className="min-w-0">
        <div className="rounded-2xl overflow-hidden mb-8 card-surface"><ComparisonVisual c={c} detail /></div>

        <QuickAnswer best={c.quick_answer} />

        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white mt-10 mb-2 tracking-tight">Side-by-Side Comparison</h2>
        <p className="text-slate-400 mb-2">How {c.items.join(", ")} compare across the features that matter most.</p>
        <ComparisonTable columns={c.columns} rows={c.rows} />

        <SectionRenderer sections={rest} />
        <FAQ faqs={c.faqs} />
        </div>
        <aside className="lg:sticky lg:top-24 h-fit"><AdSlot placement="sidebar" /></aside>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        {d.related_tools?.length > 0 && (
          <section className="mb-12">
            <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Tools in This Comparison</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{d.related_tools.map((t) => <ToolCard key={t.slug} t={t} />)}</div>
          </section>
        )}
        {d.related_comparisons?.length > 0 && (
          <section>
            <h2 className="font-display text-2xl font-bold text-white mb-5 tracking-tight">Related Comparisons</h2>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">{d.related_comparisons.map((rc) => <ComparisonCard key={rc.slug} c={rc} />)}</div>
          </section>
        )}
      </div>
    </div>
  );
}
